import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';

import LifecycleEventChips from './LifecycleEventChips';
import ToolCallProgressTimeline from './ToolCallProgressTimeline';
import AgentOrb from './AgentOrb';
import { deriveToolCallProgress } from './helpers/progress';
import { canonicalAgentKey } from './helpers/agent-keys';
import './ToolCall.scss';

// App-specific rich result treatments (e.g. the dock's finished-build plan card) are
// inverted to `renderResultCard(result, call)` — rendered above the collapsed body for
// each tool_result when the callback returns a node. Lifecycle debug chips take their
// visibility and replay handling via props (see LifecycleEventChips).
const ToolCallCard = ({ call, results, thread, renderResultCard, lifecycleEventsVisible, onReplayLifecycleEvent }) => {
  const metadata = call.metadata || {};

  // Payloads are debug context, collapsed by default; the activity line communicates
  // running / done on its own, so the body only opens on demand.
  const [isOpen, setIsOpen] = useState(false);

  // Re-derive on a 1s heartbeat while the tool is live, so the ETA counts down
  // and the stalled state can flip without a new frame arriving.
  const [now, setNow] = useState(() => Date.now());
  const progress = deriveToolCallProgress(call, { results, thread, now });
  const { status, hasTimeline } = progress;
  const isLive = hasTimeline && (status === 'running' || status === 'stalled');
  useEffect(() => {
    if (!isLive) return undefined;
    const heartbeat = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(heartbeat);
  }, [isLive]);

  const hasInput   = metadata.toolInput && Object.keys(metadata.toolInput).length > 0;
  const hasResults = Array.isArray(results) && results.length > 0;
  const hasPayload = hasInput || hasResults || hasTimeline;

  const isSettled = status !== 'running' && status !== 'stalled' && status !== 'cancelled';
  const label = getToolCallLabel(metadata.toolName, isSettled ? 'done' : 'running');

  // A sub-agent hand-off ("agent-<key>") wears the specialist's identity orb in the glyph
  // slot — the agent look — instead of the generic status dot.
  const toolId = String(metadata.toolName || '').replace(/^catalog__/, '');
  const delegationAgentKey = /^agent[-_.](.+)$/i.test(toolId) ? canonicalAgentKey(toolId) : null;

  const statusLine = (() => {
    if (status === 'stalled') return 'Still working…';
    if (status === 'cancelled') return 'Stopped';
    if (status !== 'running' || !hasTimeline) return null;
    return _.compact([progress.label, progress.countsText, progress.etaText]).join(' · ') || null;
  })();
  // The crossfade plays when the work being described changes — never on the
  // once-a-second ETA countdown, which should just quietly tick in place.
  const statusLineKey = `${status}:${progress.label}:${progress.countsText}`;

  const showTrack = hasTimeline && ['running', 'stalled', 'cancelled', 'done'].includes(status);
  const isIndeterminateTrack = progress.isIndeterminate && status !== 'done';
  const trackPct = status === 'done' ? 100 : Math.round(progress.pct || 0);

  const lifecycleEvents = [
    ...(Array.isArray(metadata.lifecycleEvents) ? metadata.lifecycleEvents : []).map(e => ({ ...e, row: call })),
    ...(results || []).flatMap(r => (r && r.metadata && Array.isArray(r.metadata.lifecycleEvents) ? r.metadata.lifecycleEvents : []).map(e => ({ ...e, row: r })))
  ];

  return (
    <div className={cn('ap-tool-call', `is-${status}`, { 'is-open': isOpen })}>
      <button
        type='button'
        className='ap-tool-call-summary'
        onClick={() => hasPayload && setIsOpen(o => !o)}
        aria-expanded={isOpen}
        disabled={!hasPayload}
      >
        {delegationAgentKey
          ? <AgentOrb agentKey={delegationAgentKey} size={16} quiet className='ap-tool-call-orb' />
          : <span className='ap-tool-call-glyph' aria-hidden='true' />}
        <span className='ap-tool-call-label'>
          {label}
          {status === 'done' && progress.elapsedText && (
            <span className='ap-tool-call-elapsed'> · {progress.elapsedText}</span>
          )}
        </span>
        {(status === 'running' || status === 'stalled') && progress.pct != null && (
          <span className='ap-tool-call-pct'>{Math.round(progress.pct)}%</span>
        )}
        {hasPayload && <span className='ap-tool-call-chevron' aria-hidden='true'>›</span>}
      </button>
      {statusLine && (
        <div className='ap-tool-call-status' key={statusLineKey}>{statusLine}</div>
      )}
      {showTrack && (
        <div
          className='ap-tool-call-track'
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={100}
          {...(isIndeterminateTrack ? {} : { 'aria-valuenow': trackPct })}
        >
          <div
            className={cn('ap-tool-call-track-fill', { 'is-indeterminate': isIndeterminateTrack })}
            style={isIndeterminateTrack ? undefined : { width: `${trackPct}%` }}
          />
        </div>
      )}
      {renderResultCard && hasResults && results.map(r => (
        <React.Fragment key={`result-card-${r.id}`}>{renderResultCard(r, call)}</React.Fragment>
      ))}
      {isOpen && (
        <div className='ap-tool-call-body'>
          {hasTimeline && <ToolCallProgressTimeline updates={progress.updates} />}
          {hasInput && (
            <pre className='ap-tool-call-input'>
              {prettyJson(metadata.toolInput)}
            </pre>
          )}
          {hasResults
            ? results.map(r => (
              <pre key={r.id} className='ap-tool-call-result'>
                {prettyJson(_.get(r, 'metadata.toolOutput'))}
              </pre>
            ))
            : !hasInput && !hasTimeline && <div className='ap-tool-call-empty'>No payload yet.</div>}
        </div>
      )}
      <LifecycleEventChips events={lifecycleEvents} visible={lifecycleEventsVisible} onReplay={onReplayLifecycleEvent} />
    </div>
  );
};

// Tool ids → human activity labels: present tense while a tool runs, past once it's done.
// Unknown ids fall back to a humanized id so the operator never sees raw `catalog__snake_case`.
// These are the personal-shopper's tools (node/routes/apiMoments.js + apiProducts.js).
const TOOL_CALL_LABELS = {
  search_products:        { running: 'Searching the catalog',       done: 'Searched the catalog' },
  get_moment:             { running: 'Reading the moment',          done: 'Read the moment' },
  save_moment:            { running: 'Saving the draft',            done: 'Saved the draft' },
  mark_moment_ready:      { running: 'Recording the judge outcome', done: 'Recorded the judge outcome' },
  list_my_moments:        { running: 'Listing the shopper’s moments', done: 'Listed the shopper’s moments' },
  start_moment_build:     { running: 'Kicking off the build',       done: 'Kicked off the build' }
};

const humanizeToolId = id => id.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

export const getToolCallLabel = (toolName, status) => {
  const id = String(toolName || 'tool').replace(/^catalog__/, '');
  // A hand-off to a specialist sub-agent arrives as a tool named "Agent-<name>" (e.g.
  // "Agent-dataAnalyst"). Phrase it like consulting that teammate rather than leaking the key.
  const agentMatch = id.match(/^agent[-_.](.+)$/i);
  if (agentMatch) {
    const specialist = _.startCase(agentMatch[1].replace(/[-_]/g, ' ')) || 'Specialist';
    return status === 'running' ? `Asking the ${specialist}` : `Asked the ${specialist}`;
  }
  const entry = TOOL_CALL_LABELS[id];
  if (!entry) return humanizeToolId(id);
  return status === 'running' ? entry.running : entry.done;
};

// Recursively walk the value and unwrap any string field that is secretly a JSON-encoded
// payload (the agent packs large objects into a `preview` string, which renders as
// "{\"found\": true, ..." after JSON.stringify and looks like commented-out code). Doing
// this once before stringifying gives readable, properly nested JSON with real quotes.
const unwrapEmbeddedJson = value => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(unwrapEmbeddedJson);
  if (typeof value === 'object') return _.mapValues(value, unwrapEmbeddedJson);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const looksLikeJson =
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'));
    if (looksLikeJson) {
      try { return unwrapEmbeddedJson(JSON.parse(trimmed)); }
      catch { /* not JSON after all — render the string verbatim */ }
    }
  }
  return value;
};

const prettyJson = value => {
  try { return JSON.stringify(unwrapEmbeddedJson(value || {}), null, 2); }
  catch { return String(value || ''); }
};

ToolCallCard.propTypes = {
  call:    PropTypes.object.isRequired,
  results: PropTypes.array,
  thread:  PropTypes.object,
  renderResultCard: PropTypes.func,
  lifecycleEventsVisible: PropTypes.bool,
  onReplayLifecycleEvent: PropTypes.func
};

export default ToolCallCard;
